import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { staffAPI, studentsAPI, modulesAPI, groupsAPI } from '../services/api';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';

// Create a simple CSS file to improve loading performance
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState({
    staffCount: 0,
    studentCount: 0,
    classCount: 0,
    moduleCount: 0,
    groupsCount: 0
  });

  const fetchDashboardData = async () => {
    try {
      console.log('=== DASHBOARD DATA FETCH DEBUG ===');
      
      // Check authentication first
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      
      // Fetch all data with individual error handling
      const results = await Promise.allSettled([
        staffAPI.getAllStaff().catch(err => {
          console.error('Staff API failed:', err);
          throw err;
        }),
        studentsAPI.getAllStudents().catch(err => {
          console.error('Students API failed:', err);
          throw err;
        }),
        modulesAPI.getAllModules().catch(err => {
          console.error('Modules API failed:', err);
          throw err;
        }),
        groupsAPI.getGroupsCount().catch(err => {
          console.error('Groups API failed:', err);
          throw err;
        })
      ]);

      console.log('API Results:', results);

      // Process each result
      const [staffResult, studentsResult, modulesResult, groupsResult] = results;

      const newStats = {
        staffCount: 0,
        studentCount: 0,
        classCount: 0,
        moduleCount: 0,
        groupsCount: 0
      };

      // Staff count - filter for active staff only
      if (staffResult.status === 'fulfilled') {
        const activeStaff = staffResult.value?.data?.filter(staff => staff.active === true) || [];
        newStats.staffCount = activeStaff.length;
        console.log('✅ Active Staff count:', newStats.staffCount);
      } else {
        console.error('❌ Staff fetch failed:', staffResult.reason);
      }

      // Students count - filter for active students only
      if (studentsResult.status === 'fulfilled') {
        const activeStudents = studentsResult.value?.data?.filter(student => student.active === true) || [];
        newStats.studentCount = activeStudents.length;
        console.log('✅ Active Students count:', newStats.studentCount);
      } else {
        console.error('❌ Students fetch failed:', studentsResult.reason);
      }

      // Modules count
      if (modulesResult.status === 'fulfilled') {
        newStats.moduleCount = modulesResult.value?.data?.length || 0;
        console.log('✅ Modules count:', newStats.moduleCount);
      } else {
        console.error('❌ Modules fetch failed:', modulesResult.reason);
      }

      // Groups count
      if (groupsResult.status === 'fulfilled') {
        const groupsCount = groupsResult.value?.data?.count || 0;
        newStats.classCount = groupsCount;
        newStats.groupsCount = groupsCount;
        console.log('✅ Groups count:', groupsCount);
        console.log('Groups API response:', groupsResult.value?.data);
      } else {
        console.error('❌ Groups fetch failed:', groupsResult.reason);
        console.error('Groups error details:', groupsResult.reason?.response?.data);
      }

      console.log('Final stats:', newStats);
      setStats(newStats);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Refresh data every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);

    return () => clearInterval(interval);
  }, []);

  // Refresh data when returning to dashboard
  useEffect(() => {
    fetchDashboardData();
  }, [location]);

  const handleAddStudent = async () => {
    try {
      // Navigate to add student page
      navigate('/add-student');
      // Update the count
      setStats(prev => ({
        ...prev,
        studentCount: prev.studentCount + 1
      }));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddLecturer = async () => {
    try {
      // Navigate to add staff page
      navigate('/add-staff');
      // Update the count
      setStats(prev => ({
        ...prev,
        staffCount: prev.staffCount + 1
      }));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAdvancedConfig = () => {
    // Handle advanced configuration click
    console.log('Advanced configuration clicked');
    // Implement navigation to configuration page or open modal here
  };

  const handleAddModule = () => navigate('/add-module');

  // New handler functions for additional buttons
  const handleAcademicManagement = () => {
    console.log('Academic Management clicked');
    // Navigate to academic management page
    navigate('/academic-management');
  };

  const handleTimeTableManagement = () => {
    console.log('Time Table Management clicked');
    // Navigate to time table management page
    navigate('/timetable-management');
  };

  const handleReportsAnalytics = () => {
    console.log('Reports & Analytics clicked');
    // Navigate to reports and analytics page
    navigate('/reports-analytics');
  };

  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(timer);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
  
  const formattedDate = currentTime.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Calendar state
  const [date, setDate] = useState(new Date());
  const [calendar, setCalendar] = useState([]);

  // Generate calendar based on current month
  useEffect(() => {
    generateCalendar();
  }, [date]);

  const generateCalendar = () => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // Get first day of month and last day
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get day of week for first day (0 = Sunday, 6 = Saturday)
    const firstDayIndex = firstDay.getDay();
    
    // Calculate total days in current month
    const daysInMonth = lastDay.getDate();
    
    // Generate array for calendar days
    const calendarDays = [];
    
    // Add empty slots for days before first day of month
    for (let i = 0; i < firstDayIndex; i++) {
      calendarDays.push(null);
    }
    
    // Add days of month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }
    
    // Create week rows (7 days per row)
    const weeks = [];
    let week = [];
    
    calendarDays.forEach((day, index) => {
      if (index % 7 === 0 && index > 0) {
        weeks.push(week);
        week = [];
      }
      week.push(day);
      
      // Add last week if we're at the end
      if (index === calendarDays.length - 1) {
        weeks.push(week);
      }
    });
    
    setCalendar(weeks);
  };

  // Navigate to previous month
  const prevMonth = () => {
    setDate(new Date(date.getFullYear(), date.getMonth() - 1, 1));
  };

  // Navigate to next month
  const nextMonth = () => {
    setDate(new Date(date.getFullYear(), date.getMonth() + 1, 1));
  };

  // Format current month and year for display
  const formatMonthYear = () => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Check if a day is today
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  // Format date for the dashboard time display
  const formatDashboardTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).replace(/\s/g, ' '); // Ensure proper spacing
  };

  // Format date as "Month Day, Year" for the dashboard
  const formatDashboardDate = () => {
    return `May 30, 2025`; // Hardcoded to match the screenshot
  };

  // Add console log to debug rendering
  console.log('AdminDashboard rendering');

  // Replace AddCard with style matching AcademicManagement ActionButton
  const AddCard = ({ title, description, onClick, iconType }) => (
    <div 
      style={{
        width: window.innerWidth <= 480 ? '100%' : window.innerWidth <= 768 ? 'calc(50% - 12px)' : 310, 
        height: window.innerWidth <= 480 ? 140 : 160, 
        background: '#E5BD9E',
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 4px 12px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.04)', 
        borderRadius: window.innerWidth <= 480 ? 16 : 24,
        position: 'relative',
        cursor: 'pointer',
        overflow: 'hidden',
        border: '2px solid none',
        transition: 'all 0.3s ease',
        marginBottom: window.innerWidth <= 480 ? '16px' : '20px',
        transform: 'translateY(0)',
      }}
      onClick={onClick}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-8px)';
        e.currentTarget.style.boxShadow = '0px 12px 32px rgba(0, 0, 0, 0.16), 0px 8px 24px rgba(0, 0, 0, 0.12), 0px 2px 6px rgba(0, 0, 0, 0.08)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 4px 12px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.04)';
      }}
      role="button"
      aria-label={`${title} — ${description}`}
    >
      {/* Background Icon */}
      <div style={{
        position: 'absolute',
        right: window.innerWidth <= 480 ? 15 : 20,
        top: window.innerWidth <= 480 ? 15 : 20,
        width: window.innerWidth <= 480 ? 60 : 74,
        height: window.innerWidth <= 480 ? 60 : 73,
        opacity: 0.5,
        zIndex: 1
      }}>
        <svg width={window.innerWidth <= 480 ? "60" : "74"} height={window.innerWidth <= 480 ? "60" : "73"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {iconType === 'student' ? (
            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#B8860B" opacity="0.3"/>
          ) : iconType === 'staff' ? (
            <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L12 2L3 7V9C3 14.55 6.84 19.74 12 21C17.16 19.74 21 14.55 21 9Z" fill="#B8860B" opacity="0.3"/>
          ) : iconType === 'module' ? (
            <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" fill="#B8860B" opacity="0.3"/>
          ) : iconType === 'academic' ? (
            <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="#B8860B" opacity="0.3"/>
          ) : iconType === 'timetable' ? (
            <path d="M9 11H7V13H9V11ZM13 11H11V13H13V11ZM17 11H15V13H17V11ZM19 3H18V1H16V3H8V1H6V3H5C3.89 3 3.01 3.9 3.01 5L3 19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19Z" fill="#B8860B" opacity="0.3"/>
          ) : iconType === 'reports' ? (
            <path d="M3.5 18.49L9 12.99L13 16.99L22 6.92L20.59 5.51L13 13.1L9 9.1L2.09 17.01L3.5 18.49Z" fill="#B8860B" opacity="0.3"/>
          ) : (
            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#B8860B" opacity="0.3"/>
          )}
        </svg>
      </div>

      <div style={{
        padding: window.innerWidth <= 480 ? '24px 20px' : '36px 24px',
        color: '#D44311',
        fontSize: window.innerWidth <= 480 ? 18 : 22,
        fontFamily: 'Outfit',
        fontWeight: '700',
        lineHeight: '28px',
        position: 'relative',
        zIndex: 2
      }}>
        {title}
      </div>

      <div style={{
        width: window.innerWidth <= 480 ? 100 : 128,
        padding: window.innerWidth <= 480 ? '6px 24px' : '8px 32px',
        background: '#F04915',
        borderRadius: 25,
        position: 'absolute',
        left: window.innerWidth <= 480 ? 24 : 32,
        bottom: window.innerWidth <= 480 ? 20 : 26,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2,
        boxShadow: '0px 4px 16px rgba(240, 73, 21, 0.4)',
        transition: 'all 0.3s ease'
      }}>
        <svg width={window.innerWidth <= 480 ? "20" : "24"} height={window.innerWidth <= 480 ? "20" : "24"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 6L15 12L9 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: 'white'
    }}>
      <Sidebar />
      
      <div style={{
        flex: 1,
        marginLeft: window.innerWidth <= 768 ? '0' : '290px',
        padding: window.innerWidth <= 480 ? '60px 15px 20px 15px' : window.innerWidth <= 768 ? '60px 20px 20px 20px' : '0 40px 40px',
        overflow: 'auto',
        transition: 'margin-left 0.3s ease'
      }}>
        <Header />
        
        {/* Welcome Section */}
        <div style={{
          background: 'linear-gradient(135deg, #F04915 0%, #FF6B35 100%)',
          borderRadius: window.innerWidth <= 480 ? 12 : 20,
          padding: window.innerWidth <= 480 ? '24px' : window.innerWidth <= 768 ? '32px' : '40px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 25px rgba(240, 73, 21, 0.2)',
          marginTop: window.innerWidth <= 480 ? '20px' : '40px',
          marginBottom: window.innerWidth <= 480 ? 24 : 32
        }}>
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: window.innerWidth <= 768 ? 150 : 200,
            height: window.innerWidth <= 768 ? 150 : 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            zIndex: 1
          }}></div>
          
          <div style={{ position: 'relative', zIndex: 2 }}>
            <h1 style={{ 
              fontSize: window.innerWidth <= 480 ? 24 : window.innerWidth <= 768 ? 28 : 36, 
              fontWeight: 700, 
              marginBottom: 8, 
              margin: 0, 
              fontFamily: 'Outfit',
              lineHeight: '1.2'
            }}>
              Welcome back, Administrator!
            </h1>
            <p style={{ 
              fontSize: window.innerWidth <= 480 ? 14 : 16, 
              margin: 0, 
              opacity: 0.9, 
              fontFamily: 'Outfit' 
            }}>
              Manage your institution with complete control and oversight
            </p>
          </div>
        </div>
        
        {/* Dashboard Widgets Section - Aligned with Management Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: window.innerWidth <= 480 ? '15px' : '25px', 
          position: 'relative', 
          marginTop: window.innerWidth <= 480 ? '40px' : '60px',
          maxWidth: '1200px',
          margin: window.innerWidth <= 480 ? '40px auto 0' : '60px auto 0',
          padding: window.innerWidth <= 480 ? '0 15px' : '0 20px',
          justifyContent: window.innerWidth <= 768 ? 'center' : 'flex-start',
          flexWrap: 'wrap',
          flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
          alignItems: window.innerWidth <= 768 ? 'center' : 'stretch'
        }}>
          {/* Clock & Date Card */}
          <div style={{
            width: window.innerWidth <= 480 ? '100%' : window.innerWidth <= 768 ? 300 : 250,
            maxWidth: window.innerWidth <= 480 ? '350px' : 'none',
            height: window.innerWidth <= 480 ? 240 : 270,
            background: '#F9F0E2',
            borderRadius: 5,
            position: 'relative',
            padding: window.innerWidth <= 480 ? '16px' : '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
              {/* Modified container to display sun icon and time in parallel */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: window.innerWidth <= 480 ? '16px' : '24px'
              }}>
                {/* Sun Icon */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'left'
                }}>
                  <svg width={window.innerWidth <= 480 ? "50" : "60"} height={window.innerWidth <= 480 ? "50" : "60"} viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24.5" cy="24.5" r="10.5" stroke="#C8CAD5" strokeWidth="2"/>
                    <line x1="24.5" y1="4" x2="24.5" y2="8" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="24.5" y1="41" x2="24.5" y2="45" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="41" y1="24.5" x2="45" y2="24.5" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="4" y1="24.5" x2="8" y2="24.5" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="36.0711" y1="12.9289" x2="38.9497" y2="10.0503" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="10.0503" y1="38.9497" x2="12.9289" y2="36.0711" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="36.0711" y1="36.0711" x2="38.9497" y2="38.9497" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                    <line x1="10.0503" y1="10.0503" x2="12.9289" y2="12.9289" stroke="#C8CAD5" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>

                {/* Time display */}
                <div style={{
                  color: '#F04915',
                  fontSize: window.innerWidth <= 480 ? 16 : 19,
                  fontFamily: 'Helvetica Neue LT Pro',
                  fontWeight: '400',
                  textAlign: 'right'
                }}>
                  {formattedTime}
                </div>
              </div>
            
            {/* Realtime Insight text */}
            <div style={{
              width: '100%',
              color: '#F04915',
              opacity: 0.50,
              fontSize: window.innerWidth <= 480 ? 10 : 12,
              fontFamily: 'Outfit',
              fontWeight: '400',
              textAlign: 'center',
              marginBottom: window.innerWidth <= 480 ? '20px' : '28px'
            }}>
              Realtime Insight
            </div>
            
            {/* Today date display */}
            <div style={{
              width: '100%',
              color: '#252C58',
              fontSize: window.innerWidth <= 480 ? 16 : 18,
              fontFamily: 'Outfit',
              fontWeight: '400',
              padding: window.innerWidth <= 480 ? '0 10px' : '0 15px',
              marginBottom: 'auto',  // Push content to top, button to bottom
              textAlign: 'center'
            }}>
              Today: <br/>{formattedDate}
            </div>
            
            {/* Advanced Configuration button - now in one line */}
            <button 
              onClick={handleAdvancedConfig}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                background: '#F04915',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: window.innerWidth <= 480 ? '6px 10px' : '8px 12px',
                fontSize: window.innerWidth <= 480 ? '11px' : '12px',
                fontFamily: 'Outfit',
                fontWeight: '500', 
                cursor: 'pointer',
                marginTop: window.innerWidth <= 480 ? '16px' : '20px',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}
            >
              <svg width={window.innerWidth <= 480 ? "12" : "14"} height={window.innerWidth <= 480 ? "12" : "14"} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '4px', flexShrink: 0}}>
                <path d="M15.2 8.8H14.176C14.112 9.152 14.016 9.488 13.888 9.808L14.624 10.544C14.944 10.864 14.944 11.376 14.624 11.696L11.696 14.624C11.376 14.944 10.864 14.944 10.544 14.624L9.808 13.888C9.488 14.016 9.152 14.112 8.8 14.176V15.2C8.8 15.648 8.448 16 8 16H5.6C5.152 16 4.8 15.648 4.8 15.2V14.176C4.448 14.112 4.112 14.016 3.792 13.888L3.056 14.624C2.736 14.944 2.224 14.944 1.904 14.624L-1.024 11.696C-1.344 11.376 -1.344 10.864 -1.024 10.544L-0.288 9.808C-0.416 9.488 -0.512 9.152 -0.576 8.8H-1.6C-2.048 8.8 -2.4 8.448 -2.4 8V5.6C-2.4 5.152 -2.048 4.8 -1.6 4.8H-0.576C-0.512 4.448 -0.416 4.112 -0.288 3.792L-1.024 3.056C-1.344 2.736 -1.344 2.224 -1.024 1.904L1.904 -1.024C2.224 -1.344 2.736 -1.344 3.056 -1.024L3.792 -0.288C4.112 -0.416 4.448 -0.512 4.8 -0.576V-1.6C4.8 -2.048 5.152 -2.4 5.6 -2.4H8C8.448 -2.4 8.8 -2.048 8.8 -1.6V-0.576C9.152 -0.512 9.488 -0.416 9.808 -0.288L10.544 -1.024C10.864 -1.344 11.376 -1.344 11.696 -1.024L14.624 1.904C14.944 2.224 14.944 2.736 14.624 3.056L13.888 3.792C14.016 4.112 14.112 4.448 14.176 4.8H15.2C15.648 4.8 16 5.152 16 5.6V8C16 8.448 15.648 8.8 15.2 8.8ZM6.8 11.2C9.12 11.2 11 9.32 11 7C11 4.68 9.12 2.8 6.8 2.8C4.48 2.8 2.6 4.68 2.6 7C2.6 9.32 4.48 11.2 6.8 11.2Z" fill="white"/>
              </svg>
              Advanced Configuration
            </button>
          </div>
        
          {/* Stats Container */}
          <div style={{ 
            display: 'flex', 
            flexDirection: window.innerWidth <= 768 ? 'column' : 'column', 
            gap: window.innerWidth <= 480 ? '16px' : '24px',
            width: window.innerWidth <= 768 ? '100%' : 'auto'
          }}>
            {/* Top Row Stats */}
            <div style={{ 
              display: window.innerWidth <= 480 ? 'flex' : 'flex', 
              gap: window.innerWidth <= 480 ? '12px' : '24px',
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
              width: '100%'
            }}>
              {/* Total Employees Card */}
              <div style={{
                width: window.innerWidth <= 480 ? '100%' : window.innerWidth <= 768 ? 'calc(50% - 12px)' : 210,
                height: window.innerWidth <= 480 ? 100 : 122,
                background: '#F9F0E2',
                borderRadius: 5,
                position: 'relative',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 6px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  position: 'absolute',
                  right: window.innerWidth <= 480 ? 10 : 15,
                  top: window.innerWidth <= 480 ? 10 : 15,
                  width: window.innerWidth <= 480 ? 28 : 35,
                  height: window.innerWidth <= 480 ? 28 : 35,
                  borderRadius: '50%',
                  background: 'rgba(240, 73, 21, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <svg width={window.innerWidth <= 480 ? "16" : "20"} height={window.innerWidth <= 480 ? "16" : "20"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#F04915"/>
                  </svg>
                </div>
                {/* Updated to match the new design */}
                <div style={{
                  left: window.innerWidth <= 480 ? 15 : 22,
                  top: window.innerWidth <= 480 ? 15 : 20,
                  position: 'absolute',
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 28 : 40,
                    fontFamily: 'Outfit',
                    fontWeight: '700',
                    lineHeight: '1'
                  }}>
                    {stats.staffCount}
                  </div>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 13 : 16,
                    fontFamily: 'Outfit',
                    fontWeight: '600'
                  }}>
                    Total Employees
                  </div>
                  {window.innerWidth > 480 && (
                    <div style={{
                      color: '#F04915',
                      fontSize: window.innerWidth <= 480 ? 10 : 12,
                      fontFamily: 'Outfit',
                      fontWeight: '400',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: 'rgba(240, 73, 21, 0.1)',
                        marginRight: '2px'
                      }}>+</span>
                      2 new employees added!
                    </div>
                  )}
                </div>
              </div>

              {/* Total Students Card */}
              <div style={{
                width: window.innerWidth <= 480 ? '100%' : window.innerWidth <= 768 ? 'calc(50% - 12px)' : 210,
                height: window.innerWidth <= 480 ? 100 : 122,
                background: '#F9F0E2',
                borderRadius: 5,
                position: 'relative',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 6px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  position: 'absolute',
                  right: window.innerWidth <= 480 ? 10 : 15,
                  top: window.innerWidth <= 480 ? 10 : 15,
                  width: window.innerWidth <= 480 ? 28 : 35,
                  height: window.innerWidth <= 480 ? 28 : 35,
                  borderRadius: '50%',
                  background: 'rgba(240, 73, 21, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <svg width={window.innerWidth <= 480 ? "16" : "20"} height={window.innerWidth <= 480 ? "16" : "20"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L1 8.5L12 15L21 9.5V15.5H23V8.5L12 2ZM3 13.5V18.5L12 23L21 18.5V13.5L12 18L3 13.5Z" fill="#F04915"/>
                  </svg>
                </div>
                {/* Updated to match the new design */}
                <div style={{
                  left: window.innerWidth <= 480 ? 15 : 22,
                  top: window.innerWidth <= 480 ? 15 : 20,
                  position: 'absolute',
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 28 : 40,
                    fontFamily: 'Outfit',
                    fontWeight: '700',
                    lineHeight: '1'
                  }}>
                    {stats.studentCount}
                  </div>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 13 : 16,
                    fontFamily: 'Outfit',
                    fontWeight: '600'
                  }}>
                    Total Students
                  </div>
                  {window.innerWidth > 480 && (
                    <div style={{
                      color: '#F04915',
                      fontSize: window.innerWidth <= 480 ? 10 : 12,
                      fontFamily: 'Outfit',
                      fontWeight: '400',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: 'rgba(240, 73, 21, 0.1)',
                        marginRight: '2px'
                      }}>+</span>
                      120 new students added!
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Row Stats */}
            <div style={{ 
              display: window.innerWidth <= 480 ? 'flex' : 'flex', 
              gap: window.innerWidth <= 480 ? '12px' : '24px',
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
              width: '100%'
            }}>
              {/* Total Classes Card */}
              <div style={{
                width: window.innerWidth <= 480 ? '100%' : window.innerWidth <= 768 ? 'calc(50% - 12px)' : 210,
                height: window.innerWidth <= 480 ? 100 : 122,
                background: '#F9F0E2',
                borderRadius: 5,
                position: 'relative',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 6px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  position: 'absolute',
                  right: window.innerWidth <= 480 ? 10 : 15,
                  top: window.innerWidth <= 480 ? 10 : 15,
                  width: window.innerWidth <= 480 ? 28 : 35,
                  height: window.innerWidth <= 480 ? 28 : 35,
                  borderRadius: '50%',
                  background: 'rgba(240, 73, 21, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <svg width={window.innerWidth <= 480 ? "16" : "20"} height={window.innerWidth <= 480 ? "16" : "20"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM19 13H5V11H19V13ZM19 9H5V7H19V9Z" fill="#F04915"/>
                  </svg>
                </div>
                <div style={{
                  left: window.innerWidth <= 480 ? 15 : 22,
                  top: window.innerWidth <= 480 ? 15 : 20,
                  position: 'absolute',
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 28 : 40,
                    fontFamily: 'Outfit',
                    fontWeight: '700',
                    lineHeight: '1'
                  }}>
                    {stats.classCount}
                  </div>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 13 : 16,
                    fontFamily: 'Outfit',
                    fontWeight: '600'
                  }}>
                    Total Groups
                  </div>
                  {window.innerWidth > 480 && (
                    <div style={{
                      color: '#F04915',
                      fontSize: window.innerWidth <= 480 ? 10 : 12,
                      fontFamily: 'Outfit',
                      fontWeight: '400',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: 'rgba(240, 73, 21, 0.1)',
                        marginRight: '2px'
                      }}>+</span>
                      Active groups in university
                    </div>
                  )}
                </div>
              </div>

              {/* Total Courses Card */}
              <div style={{
                width: window.innerWidth <= 480 ? '100%' : window.innerWidth <= 768 ? 'calc(50% - 12px)' : 210,
                height: window.innerWidth <= 480 ? 100 : 122,
                background: '#F9F0E2',
                borderRadius: 5,
                position: 'relative',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 6px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  position: 'absolute',
                  right: window.innerWidth <= 480 ? 10 : 15,
                  top: window.innerWidth <= 480 ? 10 : 15,
                  width: window.innerWidth <= 480 ? 28 : 35,
                  height: window.innerWidth <= 480 ? 28 : 35,
                  borderRadius: '50%',
                  background: 'rgba(240, 73, 21, 0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <svg width={window.innerWidth <= 480 ? "16" : "20"} height={window.innerWidth <= 480 ? "16" : "20"} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V4C20 2.9 19.1 2 18 2ZM9 4H11V9L10 8.25L9 9V4ZM18 20H6V4H7V13L10 10.75L13 13V4H18V20Z" fill="#F04915"/>
                  </svg>
                </div>
                <div style={{
                  left: window.innerWidth <= 480 ? 15 : 22,
                  top: window.innerWidth <= 480 ? 15 : 20,
                  position: 'absolute',
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 28 : 40,
                    fontFamily: 'Outfit',
                    fontWeight: '700',
                    lineHeight: '1'
                  }}>
                    {stats.moduleCount}
                  </div>
                  <div style={{
                    color: '#131A1A',
                    fontSize: window.innerWidth <= 480 ? 13 : 16,
                    fontFamily: 'Outfit',
                    fontWeight: '600'
                  }}>
                    Total Modules
                  </div>
                  {window.innerWidth > 480 && (
                    <div style={{
                      color: '#F04915',
                      fontSize: window.innerWidth <= 480 ? 10 : 12,
                      fontFamily: 'Outfit',
                      fontWeight: '400',
                      marginTop: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: 'rgba(240, 73, 21, 0.1)',
                        marginRight: '2px'
                      }}>+</span>
                      Active modules in university
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Widget */}
          <div style={{
            width: window.innerWidth <= 480 ? '100%' : window.innerWidth <= 768 ? 350 : 324,
            maxWidth: window.innerWidth <= 480 ? '350px' : 'none',
            height: window.innerWidth <= 480 ? 280 : 310,
            background: 'white',
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.08), 0px 4px 12px rgba(0, 0, 0, 0.06)',
            borderRadius: 8,
            padding: window.innerWidth <= 480 ? '20px' : '24px',
            transition: 'all 0.3s ease'
          }}>
            {/* Calendar display */}
            <div style={{width: '100%', display: 'flex', flexDirection: 'column', gap: window.innerWidth <= 480 ? '16px' : '20px'}}>
              {/* Header with arrows */}
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: window.innerWidth <= 480 ? '0' : '5px'}}>
                <button 
                  onClick={prevMonth} 
                  style={{border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                >
                  <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.41 1.41L6 0L0 6L6 12L7.41 10.59L2.83 6L7.41 1.41Z" fill="#B5BEC6"/>
                  </svg>
                </button>
                <div style={{color: '#4A5660', fontSize: window.innerWidth <= 480 ? '13px' : '14px', fontWeight: '600', fontFamily: 'Outfit, sans-serif'}}>
                  {formatMonthYear()}
                </div>
                <button 
                  onClick={nextMonth} 
                  style={{border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                >
                  <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 0L0.59 1.41L5.17 6L0.59 10.59L2 12L8 6L2 0Z" fill="#B5BEC6"/>
                  </svg>
                </button>
              </div>

              {/* Day names - just like the screenshot */}
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: window.innerWidth <= 480 ? '8px' : '10px'}}>
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <div key={day} style={{
                    fontSize: window.innerWidth <= 480 ? '9px' : '10px', 
                    color: '#B5BEC6', 
                    fontWeight: '600',
                    textTransform: 'uppercase', 
                    letterSpacing: '1px',
                    fontFamily: 'Outfit, sans-serif'
                  }}>
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridGap: window.innerWidth <= 480 ? '6px 0' : '8px 0', textAlign: 'center'}}>
                {calendar.map((week, weekIndex) => (
                  <React.Fragment key={`week-${weekIndex}`}>
                    {week.map((day, dayIndex) => {
                      // Check if this day is today
                      const isCurrentDay = isToday(day);
                      
                      return (
                        <div 
                          key={`day-${weekIndex}-${dayIndex}`}
                          style={{
                            height: window.innerWidth <= 480 ? '20px' : '24px', 
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          {day !== null ? (
                            isCurrentDay ? (
                              <div style={{
                                width: window.innerWidth <= 480 ? '26px' : '30px',
                                height: window.innerWidth <= 480 ? '26px' : '30px',
                                borderRadius: '50%',
                                backgroundColor: '#F04915',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}>
                                <span style={{
                                  color: 'white',
                                  fontSize: window.innerWidth <= 480 ? '12px' : '14px',
                                  fontWeight: '600',
                                  fontFamily: 'Outfit, sans-serif'
                                }}>
                                  {day}
                                </span>
                              </div>
                            ) : (
                              <span style={{
                                fontSize: window.innerWidth <= 480 ? '12px' : '14px',
                                color: '#4A5660',
                                fontWeight: '600',
                                fontFamily: 'Outfit, sans-serif'
                              }}>
                                {day}
                              </span>
                            )
                          ) : (
                            <span style={{color: 'transparent'}}>&nbsp;</span>
                          )}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Add Cards Section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', 
          gap: window.innerWidth <= 480 ? '16px' : '25px', 
          position: 'relative', 
          marginTop: window.innerWidth <= 480 ? '120px' : '150px',
          maxWidth: '1200px',
          margin: window.innerWidth <= 480 ? '120px auto 0' : '150px auto 0',
          padding: window.innerWidth <= 480 ? '0 15px' : '0 20px'
        }}>
          <AddCard 
            title="Student Management" 
            description="Manage student records and enrollment"
            onClick={handleAddStudent}
            iconType="student"
          />
          <AddCard 
            title="Staff Management" 
            description="Handle staff information and roles"
            onClick={handleAddLecturer}
            iconType="staff"
          />
          <AddCard 
            title="Module Management" 
            description="Organize courses and modules"
            onClick={handleAddModule}
            iconType="module"
          />
          <AddCard 
            title="Academic Management" 
            description="Academic programs and curriculum"
            onClick={handleAcademicManagement}
            iconType="academic"
          />
          <AddCard 
            title="Time Table Management" 
            description="Schedule classes and events"
            onClick={handleTimeTableManagement}
            iconType="timetable"
          />
          <AddCard 
            title="Reports & Analytics" 
            description="View insights and generate reports"
            onClick={handleReportsAnalytics}
            iconType="reports"
          />
        </div>

      </div>
    </div>
  );
};

export default AdminDashboard;
